import { BaseError } from "./base-error";

/**
 * Error class for application-level error handling with status codes.
 *
 * Provides structured error information with factory methods for each HTTP status code.
 * This provides type-safe, validated error creation that can be used in API responses.
 */
export class ApplicationError extends BaseError {
	readonly statusCode: number;
	readonly code: string;

	constructor(
		statusCode: number,
		message: string,
		code = "APPLICATION_ERROR",
		details?: Record<string, unknown>,
	) {
		super(message, details);
		this.statusCode = statusCode;
		this.code = code;
	}

	/**
	 * Create a bad request error (400)
	 */
	static badRequest(
		message: string,
		code?: string,
		details?: Record<string, unknown>,
	): ApplicationError {
		return new ApplicationError(400, message, code, details);
	}

	/**
	 * Create an unauthorized error (401)
	 */
	static unauthorized(
		message = "Unauthorized",
		code?: string,
		details?: Record<string, unknown>,
	): ApplicationError {
		return new ApplicationError(401, message, code, details);
	}

	/**
	 * Create a forbidden error (403)
	 */
	static forbidden(
		message = "Forbidden",
		code?: string,
		details?: Record<string, unknown>,
	): ApplicationError {
		return new ApplicationError(403, message, code, details);
	}

	/**
	 * Create a not found error (404)
	 */
	static notFound(
		message = "Resource not found",
		code?: string,
		details?: Record<string, unknown>,
	): ApplicationError {
		return new ApplicationError(404, message, code, details);
	}

	/**
	 * Create a conflict error (409)
	 */
	static conflict(
		message: string,
		code?: string,
		details?: Record<string, unknown>,
	): ApplicationError {
		return new ApplicationError(409, message, code, details);
	}

	/**
	 * Create an unprocessable entity error (422)
	 */
	static unprocessableEntity(
		message: string,
		code?: string,
		details?: Record<string, unknown>,
	): ApplicationError {
		return new ApplicationError(422, message, code, details);
	}

	/**
	 * Create an internal server error (500)
	 */
	static internalServerError(
		message = "Internal server error",
		code?: string,
		details?: Record<string, unknown>,
	): ApplicationError {
		return new ApplicationError(500, message, code, details);
	}

	/**
	 * Create a service unavailable error (503)
	 */
	static serviceUnavailable(
		message = "Service unavailable",
		code?: string,
		details?: Record<string, unknown>,
	): ApplicationError {
		return new ApplicationError(503, message, code, details);
	}

	/**
	 * Check if error is a client error (4xx)
	 */
	isClientError(): boolean {
		return this.statusCode >= 400 && this.statusCode < 500;
	}

	/**
	 * Check if error is a server error (5xx)
	 */
	isServerError(): boolean {
		return this.statusCode >= 500 && this.statusCode < 600;
	}

	toString(): string {
		return `${this.statusCode}: ${this.message}`;
	}

	/**
	 * Reconstruct an ApplicationError from its JSON representation.
	 * Useful for deserializing errors sent over the network or stored.
	 */
	static fromJSON(data: {
		name: string;
		message: string;
		code: string;
		statusCode: number;
		timestamp: string;
		details?: Record<string, unknown>;
	}): ApplicationError {
		const error = new ApplicationError(
			data.statusCode,
			data.message,
			data.code,
			data.details,
		);
		// Override the auto-generated timestamp with the original
		Object.defineProperty(error, "timestamp", {
			value: new Date(data.timestamp),
			writable: false,
		});
		return error;
	}

	// ============================================
	// Static factory methods for domain-specific errors
	// These provide convenient methods for creating common error types
	// with consistent messaging patterns.
	// ============================================

	/**
	 * Create a not found error for a specific entity
	 */
	static entityNotFound(entity: string, id: string): ApplicationError {
		return new ApplicationError(
			404,
			`${entity} with ID ${id} not found`,
			"NOT_FOUND",
			{
				entity,
				id,
			},
		);
	}

	/**
	 * Create an unauthorized error for an action on a specific entity
	 */
	static entityUnauthorized(
		entity: string,
		id: string,
		action = "modify",
	): ApplicationError {
		return new ApplicationError(
			403,
			`You don't have permission to ${action} ${entity.toLowerCase()} with ID ${id}`,
			"UNAUTHORIZED",
			{ entity, id, action },
		);
	}

	/**
	 * Create a creation failed error
	 */
	static createFailed(entity: string): ApplicationError {
		return new ApplicationError(
			500,
			`Failed to create ${entity.toLowerCase()}`,
			"CREATE_FAILED",
			{
				entity,
			},
		);
	}

	/**
	 * Create an update failed error
	 */
	static updateFailed(entity: string, id: string): ApplicationError {
		return new ApplicationError(
			500,
			`Failed to update ${entity.toLowerCase()} with ID ${id}`,
			"UPDATE_FAILED",
			{ entity, id },
		);
	}

	/**
	 * Create a delete failed error
	 */
	static deleteFailed(entity: string, id: string): ApplicationError {
		return new ApplicationError(
			500,
			`Failed to delete ${entity.toLowerCase()} with ID ${id}`,
			"DELETE_FAILED",
			{ entity, id },
		);
	}

	/**
	 * Create a validation error
	 */
	static validation(message: string): ApplicationError {
		return new ApplicationError(422, message, "VALIDATION_ERROR");
	}

	/**
	 * Create a server error
	 */
	static server(message: string): ApplicationError {
		return new ApplicationError(500, message, "SERVER_ERROR");
	}
}
